// src/modules/public/pages/LoginPage/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useAlert } from '../../../../shared/hooks/useAlert';
import LoginForm from '../../components/LoginForm';
import Loading from '../../../../shared/components/Loading';
import apiService from '../../../../shared/services/apiService';
import './styles.css';

const USE_LOCAL_AUTH = process.env.REACT_APP_USE_LOCAL_AUTH === 'true';

const LoginPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState('');
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { alert, showSuccess, showError, hideAlert } = useAlert();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/fithub');
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (location.state?.successMessage) {
            showSuccess(location.state.successMessage);
        }
    }, [location.state?.successMessage, showSuccess]);

    // Fetch available users in local dev mode
    const loadAvailableUsers = useCallback(async () => {
        if (!USE_LOCAL_AUTH) return;
        try {
            const response = await apiService.get('/admin/reset/users');
            setAvailableUsers(response.users || []);
        } catch (error) {
            // Silently fail — this is just a convenience feature
            console.log('Could not load available users:', error.message);
        }
    }, []);

    useEffect(() => {
        loadAvailableUsers();
    }, [loadAvailableUsers]);

    const handleLogin = async (credentials) => {
        setIsLoading(true);
        try {
            await login(credentials);
            navigate('/fithub');
        } catch (error) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUserClick = async (email) => {
        setSelectedEmail(email);
        await handleLogin({ email, password: 'local' });
    };

    return (
        <Container className="mt-5">
            {isLoading ? (
                <Loading></Loading>
            ) : (
                <>
                    <LoginForm
                        onSubmit={handleLogin}
                        isLoading={isLoading}
                        alert={alert}
                        onAlertClose={hideAlert}
                        initialEmail={selectedEmail}
                    />
                    {USE_LOCAL_AUTH && (
                        <div className="available-users">
                            {availableUsers.length > 0 ? (
                                <>
                                    <p className="available-users-title">Available test users:</p>
                                    <div className="available-users-list">
                                        {availableUsers.map(user => (
                                    <button
                                        key={user.username}
                                        type="button"
                                        className={`available-user-btn ${selectedEmail === user.email ? 'selected' : ''}`}
                                        onClick={() => handleUserClick(user.email)}
                                    >
                                        <div className="user-info">
                                            <span className="user-name">{user.firstName} {user.lastName}</span>
                                            <span className="user-email">{user.email}</span>
                                        </div>
                                        {user.permissions && user.permissions.length > 0 && (
                                            <div className="user-permissions">
                                                {user.permissions.map(p => (
                                                    <span key={p} className="user-permission-badge">{p}</span>
                                                ))}
                                            </div>
                                        )}
                                    </button>
                                ))}
                                </div>
                                </>
                            ) : (
                                <p className="available-users-title text-warning">
                                    <i className="bi bi-database-x me-1"></i>
                                    No test users found. Run <code>task local:reset-and-seed</code> to seed the local database.
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
        </Container>
    );
};

export default LoginPage;
